import Checklist, { PersonalInfoSupportingItemInput } from 'jobs/Checklist'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import Job from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { whileActive, withCancellation } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { task } from 'mobx-task'
import IndividualProfileStore from '../IndividualProfileStore'
import { BehaviorSubject } from 'rxjs'

export default class PersonalProfileViewModel {
  // Tracks whether the view model is active.
  private readonly _active = new BehaviorSubject<boolean>(false)

  constructor(
    public job: Job,
    public infoGatheringStore: InfoGatheringStore,
    public individualProfileStore: IndividualProfileStore,
    private getChecklist: () => Checklist | undefined,
  ) {
    makeObservable(this)

    // reacts to changes in the supporting item to re-fetch the profile.
    toStream(() => {
      return {
        profileVersion: this.personalInfoSupportingItemInput?.profileVersion,
        profileId: this.personalInfoSupportingItemInput?.profileId,
      }
    }, true)
      .pipe(
        whileActive(this._active),
        switchMap(() => withCancellation(this._onRemoteItemUpdated)),
      )
      .subscribe()
  }

  /**
   * The personal profile version.
   */
  @computed
  get profile() {
    if (!this.personalInfoSupportingItemInput?.profileVersion) {
      return null
    }

    return this.individualProfileStore.getPersonalProfile(
      this.personalInfoSupportingItemInput?.profileId,
      this.personalInfoSupportingItemInput?.profileVersion,
    )
  }

  /**
   * The checklist.
   */
  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * The personal info section.
   */
  @computed
  get personalInfoSection() {
    return this.checklist?.personalInfoSection
  }

  /**
   * The supporting item.
   */
  @computed
  get personalInfoSupportingItem() {
    return this.personalInfoSection?.personalInfoSupportingItem
  }

  /**
   * The supporting item input.
   */
  @computed
  get personalInfoSupportingItemInput() {
    return this.personalInfoSupportingItem
      ?.input as PersonalInfoSupportingItemInput
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this._active.next(true)

    if (this.personalInfoSupportingItem?.completed) {
      await this.individualProfileStore.fetchPersonalProfileForJob(this.job.id)
    }
  })

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Updates the personal profile when remotes update happens.
   */
  private _onRemoteItemUpdated = task(async (ct: CancellationToken) => {
    if (this.personalInfoSupportingItem?.completed) {
      await ct.race(
        this.individualProfileStore.fetchPersonalProfileForJob(this.job.id),
      )
    }
  })
}
